import { useEffect, useRef, useState } from "react";
import { useAuth } from "@/components/auth/AuthContext";
import { Button } from "@/components/ui/Button";
import { inventoryCapabilities } from "@/lib/inventory/capabilities";
import { listSerialExceptions, type SerialExceptionsPage, type SerialException } from "@/lib/inventory/allocationsApi";
import { allocationErrorText, allocationFailure, invalidAllocationResponse, useAllocationsText, type AllocationFailure } from "@/lib/inventory/allocationsI18n";

/** Read-only projection; deliberately no invoice mutation or finance-export action. */
export function InventorySerialExceptions() {
  const auth = useAuth();
  const { issuer, sub } = auth.user ?? {};
  const valid = typeof issuer === "string" && issuer.length > 0 && !/[\s\u0000-\u001f\u007f]/.test(issuer)
    && typeof sub === "string" && sub.length > 0 && sub.length <= 255 && !/[\s\u0000-\u001f\u007f]/.test(sub);
  if (!auth.isReady || !auth.isAuthenticated || !inventoryCapabilities(auth.groups).read || !valid) return null;
  return <SerialExceptionsSession key={JSON.stringify([issuer, sub])} />;
}
function SerialExceptionsSession() {
  const auth = useAuth(), t = useAllocationsText();
  const canRead = auth.isReady && auth.isAuthenticated && inventoryCapabilities(auth.groups).read;
  const [data, setData] = useState<SerialExceptionsPage | null>(null);
  const [loading, setLoading] = useState(false), [error, setError] = useState<AllocationFailure | null>(null);
  const sequence = useRef(0);
  async function load(offset: number) {
    const token = ++sequence.current; setLoading(true); setError(null);
    try {
      const result = await listSerialExceptions(50, offset);
      if (token !== sequence.current) return;
      if (!Array.isArray(result.export_exceptions) || !Array.isArray(result.reimport_exceptions)) throw invalidAllocationResponse;
      setData(result);
    } catch (e) {
      if (token === sequence.current) setError(allocationFailure(e));
    } finally { if (token === sequence.current) setLoading(false); }
  }
  useEffect(() => {
    setData(null);
    if (canRead) void load(0);
    return () => { sequence.current++; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canRead]);
  function report(title: string, rows: SerialException[]) {
    return <section aria-label={title}><h4>{title}</h4>{rows.length ? <table className="w-full text-left text-sm">
      <thead><tr><th>{t.order}</th><th>{t.code}</th><th>{t.detail}</th></tr></thead>
      <tbody>{rows.map((r, i) => <tr key={r.exception_id ?? `${r.order_id}-${i}`}><td><code>{r.order_id}</code></td><td><code>{r.code}</code></td><td>{r.detail ?? "—"}</td></tr>)}</tbody>
    </table> : <p>{t.empty}</p>}</section>;
  }
  if (!canRead) return null;
  return <section className="space-y-3" aria-label={t.exceptions}>
    <h3>{t.exceptions}</h3><p>{t.exceptionHelp}</p><p>{t.unverified}</p>
    <Button disabled={loading} onClick={() => void load(data?.offset ?? 0)}>{t.refresh}</Button>
    {loading && <p role="status">{t.loading}</p>}{error && <p role="alert">{allocationErrorText(t, error)}</p>}
    {data && <>{report(t.exportExceptions, data.export_exceptions)}{report(t.reimportExceptions, data.reimport_exceptions)}
      <Button disabled={loading || data.offset === 0} onClick={() => void load(Math.max(0, data.offset - 50))}>{t.previous}</Button>
      <Button disabled={loading || !(data.has_more || data.reimport_has_more)} onClick={() => void load(data.offset + 50)}>{t.next}</Button>
    </>}
  </section>;
}
